// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@keller/XDL/constants";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridLayoutContextType {
    styles: ProductGridLayoutStyle;
}
export interface ProductGridLayoutStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    priceSection?: InjectableCss;
    availabilitySection?: InjectableCss;
    addToListSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    image?: InjectableCss;
    addToCartVariantParent?: InjectableCss;
}

const { productGridView: productGridViewScreen } = constants;
const productGridLayoutStyle: ProductGridLayoutStyle = {
    container: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            margin: 0 auto;

            max-width: ${productGridViewScreen.desktop.width}px;
            ${xScreen("lte", "md")} {
                max-width: ${productGridViewScreen.tablet.width}px;
            }

            ${xScreen("lte", "xs")} {
                max-width: ${productGridViewScreen.mobile.width}px;
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            background-color: ${color("common.white")};
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 12px;

            height: ${productGridViewScreen.desktop.height}px;
            ${xScreen("lte", "md")} {
                height: ${productGridViewScreen.tablet.height}px;
            }
            ${xScreen("lte", "xs")} {
                height: ${productGridViewScreen.mobile.height}px;
            }
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 12px;
        `,
    },
    priceSection: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: auto;
            > *:not(:empty) {
                margin-bottom: 12px;
            }
        `,
    },
    availabilitySection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0;
            > *:not(:empty) {
                margin-bottom: 12px;
                &:not(:last-child) {
                    margin-right: 20px;
                }
            }
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            > * {
                margin-bottom: 16px;
                &:not(:last-child) {
                    margin-right: 20px;
                }
            }
        `,
    },
    addToListSection: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: space-between;
        `,
    },
    addToCartVariantParent: {
        css: css`
            button {
                width: 100%;
            }
        `,
    },
};

const ProductGridLayoutContext = React.createContext<ProductGridLayoutContextType>({
    styles: {},
});

export const useProductGridLayoutContext = () => {
    const context = React.useContext(ProductGridLayoutContext);
    if (!context) {
        throw new Error("ProductGridLayout compound components cannot be rendered outside ProductGridLayout");
    }
    return context;
};

const ProductGridLayout = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: ProductGridLayoutStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridLayoutStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <ProductGridLayoutContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps}>
                {children}
            </StyledWrapper>
        </ProductGridLayoutContext.Provider>
    );
};

const Image: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...image}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...info}>
            {children}
        </StyledWrapper>
    );
};

const PriceSection: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { priceSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...priceSection}>
            {children}
        </StyledWrapper>
    );
};

const AvailabilitySection: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { availabilitySection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...availabilitySection}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartSection: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartSection}>
            {children}
        </StyledWrapper>
    );
};

const AddToListAction: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToListSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToListSection} data-test-selector="productCarousel-AddToListActions">
            {children}
        </StyledWrapper>
    );
};
const AddToCartVariantParent: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartVariantParent },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper
            {...otherProps}
            {...addToCartVariantParent}
            data-test-selector="productCarousel-AddToCartVariantParent"
        >
            {children}
        </StyledWrapper>
    );
};

ProductGridLayout.Image = Image;
ProductGridLayout.Info = Info;
ProductGridLayout.PriceSection = PriceSection;
ProductGridLayout.AvailabilitySection = AvailabilitySection;
ProductGridLayout.AddToCartSection = AddToCartSection;
ProductGridLayout.AddToListAction = AddToListAction;
ProductGridLayout.AddToCartVariantParent = AddToCartVariantParent;

export default ProductGridLayout;
